import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum IncidentType {
  None = 'None',
  RearEnd = 'Rear End',
  FixedPropertySingleVehicle = 'Fixed Property - Single Vehicle',
  IntersectionLTTurn = 'Intersection - LT Turn',
  IntersectionOther = 'Intersection - Other',
  Backing = 'Backing',
  LaneChange = 'Lane Change',
  LeftOfCenter = 'Left of Center',
  CompDeerHit = 'COMP - Deer Hit',
  CompWeatherVandalism = 'COMP - Weather/Vandalism',
  SingleVehicleNoFixedPD = 'Single Vehicle - No Fixed PD',
  OtherSeeNotes = 'OTHER - See Notes',
}

interface IncidentDetailsState {
  incidentId: number;
  incidentDate: Date;
  policeCaseNumber: string;
  accidentLocationDescription: string;
  additionalSafetyNotes: string;
  injuryComplaints: boolean;
  incidentType: IncidentType | '';
  otherParties: string;
  otherProperty: string;
  otherVehicles: string;
  isFinalized: boolean;
}

const initialState: IncidentDetailsState = {
  incidentId: 0,
  incidentDate: new Date(),
  policeCaseNumber: '',
  accidentLocationDescription: '',
  additionalSafetyNotes: '',
  injuryComplaints: false,
  incidentType: '',
  otherParties: '',
  otherProperty: '',
  otherVehicles: '',
  isFinalized: false
};

const incidentDetailsSlice = createSlice({
  name: 'incidentDetails',
  initialState,
  reducers: {
    setIncidentId: (state, action: PayloadAction<number>) => {
      state.incidentId = action.payload;
    },
    setIncidentDate: (state, action: PayloadAction<Date | string>) => {
      state.incidentDate = typeof action.payload === 'string' 
        ? new Date(action.payload) 
        : action.payload;
    },
    setPoliceCaseNumber: (state, action: PayloadAction<string>) => {
      state.policeCaseNumber = action.payload;
    },
    setAccidentLocation: (state, action: PayloadAction<string>) => {
      state.accidentLocationDescription = action.payload;
    },
    setAdditionalSafetyNotes: (state, action: PayloadAction<string>) => {
      state.additionalSafetyNotes = action.payload;
    },
    setInjuryComplaints: (state, action: PayloadAction<boolean>) => {
      state.injuryComplaints = action.payload;
    },
    setIncidentType: (state, action: PayloadAction<IncidentType | ''>) => {
      state.incidentType = action.payload;
    },
    setOtherParties: (state, action: PayloadAction<string>) => {
      state.otherParties = action.payload;
    },
    setOtherProperty: (state, action: PayloadAction<string>) => {
      state.otherProperty = action.payload;
    },
    setOtherVehicles: (state, action: PayloadAction<string>) => {
      state.otherVehicles = action.payload;
    },
    setIsFinalized: (state, action: PayloadAction<boolean>) => {
      state.isFinalized = action.payload;
    },
    setAllIncidentDetails: (state, action: PayloadAction<Partial<IncidentDetailsState>>) => {
      return { ...state, ...action.payload };
    }
  }
});

export const {
  setIncidentId,
  setIncidentDate,
  setPoliceCaseNumber,
  setAccidentLocation,
  setAdditionalSafetyNotes,
  setInjuryComplaints,
  setIncidentType,
  setOtherParties,
  setOtherProperty,
  setOtherVehicles,
  setIsFinalized,
  setAllIncidentDetails
} = incidentDetailsSlice.actions;

export default incidentDetailsSlice.reducer;

// Helper functions
export const getIncidentTypeOptions = (): string[] => {
  return Object.values(IncidentType);
};

export const isIncidentDetailsComplete = (state: IncidentDetailsState): boolean => {
  // Required fields for operator role
  return (
    state.incidentType !== '' &&
    state.accidentLocationDescription.trim() !== ''
  );
};